/**
 * lib/parentNotices.ts
 * ------------------------------------------------------------------
 * Core types + pure recipient resolution for the Parent Notices
 * feature (school -> parent broadcast, and teacher-scoped notices).
 *
 * This module is intentionally dependency-free: it imports nothing
 * from Firebase or the app, so it can be reused unchanged by
 *   - the admin/head composer (resolves client-side from the loaded
 *     `users` directory), and
 *   - a client-side live preview count,
 * and mirrored by the Cloud Function that re-resolves teacher-scoped
 * notices server-side (the function reimplements the same algorithm in
 * JS, since functions/ is a separate package and cannot import src/).
 *
 * APP-SPECIFIC NOTE (differs from the sibling app): an LKC `Student`
 * carries `currentClass` (a classId) but NO `form` field. Grade/form
 * lives on `SchoolClass.form`, so the `grades` audience resolves
 * form -> matching class ids -> students in those classes. The resolver
 * student shape is therefore `{ id, currentClass }`, not
 * `{ id, currentClass, form }`.
 * ------------------------------------------------------------------
 */

/* ============================ constants ============================ */

/** Firestore collection for broadcast notices (report* prefix; see DataContext). */
export const PARENT_NOTICES_COLLECTION = 'reportParentNotices';

/** Per-parent read-receipt subcollection under a notice doc. */
export const PARENT_NOTICE_READS_SUBCOLLECTION = 'reads';

/* ============================== types ============================== */

export type NoticePriority = 'normal' | 'high';

/** 'school' = admin/head broadcast; 'teacher' = author-scoped (Part B). */
export type NoticeScope = 'school' | 'teacher';

/** Teacher-scoped notice flavour (Part B). */
export type NoticeType = 'general' | 'homework';

export type NoticeAudienceKind = 'school' | 'classes' | 'grades' | 'students';

/**
 * Who a notice targets. `kind` selects which of the optional id arrays
 * is meaningful:
 *   - 'school'   -> none (every pupil)
 *   - 'classes'  -> classIds
 *   - 'grades'   -> forms (e.g. ['3','4'])
 *   - 'students' -> studentIds
 */
export interface NoticeAudience {
  kind: NoticeAudienceKind;
  classIds?: string[];
  forms?: string[];
  studentIds?: string[];
}

/* ------------------------- attachments -------------------------
   A notice can carry files: a signed letter from the Principal as a PDF,
   or photographs for something being advertised. Deliberately a small,
   closed set of types — a broadcast reaches every parent in the school, so
   whatever is attached is the one thing the school cannot take back.

   Files live in Storage at `parentNotices/{noticeId}/{fileName}` and the
   notice carries only the metadata. `path` is kept alongside `url` because
   a tokenised download URL cannot be turned back into a storage location,
   and without it an attachment can never be removed. */

export interface NoticeAttachment {
  /** The name a parent sees and downloads as. */
  name: string;
  /** Tokenised download URL — what the portal and the email link to. */
  url: string;
  /** Storage path, for deletion and audit. Never shown to a parent. */
  path: string;
  contentType: string;
  /** Bytes. Shown to a parent so nobody opens 8MB on mobile data unaware. */
  size: number;
}

/** What may be attached. PDFs for letters, images for posters and flyers. */
export const NOTICE_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

export const NOTICE_ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp';

/** Per file. Enforced in the composer AND in storage.rules — the rule is
 *  the one that counts, the composer message is the one that helps. */
export const NOTICE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Per notice. Beyond a handful this stops being a notice and becomes a
 *  mailing nobody reads on a phone. */
export const NOTICE_ATTACHMENT_MAX_COUNT = 5;

export function isImageAttachment(a: { contentType?: string }): boolean {
  return String(a.contentType || '').startsWith('image/');
}

/** "2.4 MB" — parents on mobile data deserve to know before tapping. */
export function formatBytes(n: number): string {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Why a file cannot be attached, or null if it can.
 * Shared by the composer and its tests so the message a user sees and the
 * rule that is enforced are the same sentence.
 */
export function attachmentRejection(file: { name: string; type: string; size: number }): string | null {
  if (!(NOTICE_ATTACHMENT_TYPES as readonly string[]).includes(file.type)) {
    return `${file.name}: only PDF, JPG, PNG and WebP files can be attached.`;
  }
  if (file.size > NOTICE_ATTACHMENT_MAX_BYTES) {
    return `${file.name} is ${formatBytes(file.size)} — the limit is ${formatBytes(NOTICE_ATTACHMENT_MAX_BYTES)} per file.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  return null;
}

/**
 * Strip a filename down to something safe to put in a Storage path and in a
 * Content-Disposition header. Keeps the extension, which is what decides
 * whether a parent's phone offers to open it in a PDF reader.
 */
export function safeAttachmentName(name: string): string {
  const raw = String(name || 'attachment').trim();
  const dot = raw.lastIndexOf('.');
  const stem = (dot > 0 ? raw.slice(0, dot) : raw)
    .replace(/[^\w\-. ]+/g, '_')
    // A full stop is legitimate inside a name ("Snr. Sales Rep"), so it is in
    // the allowed set — which leaves "../../etc/passwd" as ".._.._etc_passwd"
    // once the separators are gone. No traversal survives that (there is no
    // slash left), but a name starting with dots is hidden on most systems
    // and reads as a mistake. Collapse runs of them and never lead with one.
    .replace(/\.{2,}/g, '.')
    .replace(/^[.\s]+/, '')
    .slice(0, 60)
    .trim() || 'attachment';
  const ext = (dot > 0 ? raw.slice(dot + 1) : '').replace(/[^\w]+/g, '').slice(0, 8).toLowerCase();
  return ext ? `${stem}.${ext}` : stem;
}

/**
 * A broadcast notice document (`reportParentNotices/{noticeId}`).
 *
 * `recipientParentUids` is the authoritative delivery list:
 *   - school scope -> resolved client-side by the admin/head composer
 *     (they load the full `users` directory).
 *   - teacher scope -> resolved SERVER-SIDE by the onCreate function,
 *     because teachers cannot read `users` in this app. The composer
 *     writes the `audience` only; the function stamps the uids + count.
 */
export interface ParentNotice {
  id: string;
  title: string;
  body: string;
  priority: NoticePriority;
  audience: NoticeAudience;
  /** Human summary, e.g. "Form 3 & Form 4" or "3 classes". */
  audienceLabel: string;
  recipientParentUids: string[];
  recipientCount: number;
  emailOnPublish: boolean;
  createdBy: string; // author uid
  createdByName: string;
  createdByRole?: string;

  /* ---- Part B (teacher-scoped notices) ---- */
  scope?: NoticeScope;
  department?: string | null;
  noticeType?: NoticeType;

  /** Files sent with the notice. Absent on every notice raised before
   *  attachments existed, so always optional. */
  attachments?: NoticeAttachment[];

  createdAt: string; // ISO
  emailedAt?: string; // ISO; set once by the email function (idempotent)
  emailedCount?: number;
  /** Stamped by the publish trigger once web push has fanned out. Absent on
   *  every notice before Aug 2026, when notices did not push at all. */
  pushedAt?: string;
  pushedCount?: number;
}

/** Per-parent read receipt: `reportParentNotices/{id}/reads/{parentUid}`. */
export interface NoticeRead {
  readAt: string; // ISO
  parentName?: string;
}

/* ----------------------- resolver inputs ----------------------- */

/** Minimal parent shape for resolution. `uid` MUST be the auth uid. */
export interface ResolverParent {
  uid: string;
  studentIds: string[];
  parentName?: string;
}

/** Minimal student shape — LKC students have no `form` (see header). */
export interface ResolverStudent {
  id: string;
  currentClass: string; // classId
}

/** Minimal class shape — `form` is the grade level. */
export interface ResolverClass {
  id: string;
  form: string;
}

export interface ResolveInputs {
  parents: ResolverParent[];
  students: ResolverStudent[];
  classes: ResolverClass[];
}

export interface ResolveResult {
  /** De-duplicated reached parent auth uids. */
  parentUids: string[];
  /** The resolved target student id set. */
  studentIds: string[];
  /** The reached parents, de-duplicated by uid. */
  parents: ResolverParent[];
}

/* =========================== resolution =========================== */

/**
 * Resolve a notice audience to the set of parents it reaches.
 *
 * Step 1 — compute the target student id set:
 *   school   -> every student id
 *   students -> audience.studentIds (verbatim)
 *   classes  -> students whose currentClass is in audience.classIds
 *   grades   -> classes whose form is in audience.forms, then students
 *               whose currentClass is in those class ids
 *
 * Step 2 — a parent is reached if ANY of their studentIds is in the
 * target set. (For 'school', that's every parent with >=1 linked child.)
 * Returns the de-duplicated reached parent uids.
 */
export function resolveRecipients(
  audience: NoticeAudience,
  { parents, students, classes }: ResolveInputs,
): ResolveResult {
  // --- Step 1: target student ids ---
  let targetIds: Set<string>;
  switch (audience.kind) {
    case 'school':
      targetIds = new Set(students.map((s) => s.id));
      break;
    case 'students':
      targetIds = new Set(audience.studentIds ?? []);
      break;
    case 'classes': {
      const classIds = new Set(audience.classIds ?? []);
      targetIds = new Set(
        students.filter((s) => classIds.has(s.currentClass)).map((s) => s.id),
      );
      break;
    }
    case 'grades': {
      const forms = new Set(audience.forms ?? []);
      const classIds = new Set(
        classes.filter((c) => forms.has(c.form)).map((c) => c.id),
      );
      targetIds = new Set(
        students.filter((s) => classIds.has(s.currentClass)).map((s) => s.id),
      );
      break;
    }
    default:
      targetIds = new Set<string>();
  }

  // --- Step 2: reached parents (de-duplicated by uid) ---
  const seen = new Set<string>();
  const reached: ResolverParent[] = [];
  for (const p of parents) {
    if (!p.uid || seen.has(p.uid)) continue;
    if (p.studentIds.some((sid) => targetIds.has(sid))) {
      seen.add(p.uid);
      reached.push(p);
    }
  }

  return {
    parentUids: reached.map((p) => p.uid),
    studentIds: Array.from(targetIds),
    parents: reached,
  };
}

/* ============================= helpers ============================= */

/**
 * Human-readable audience summary for `audienceLabel`. Pure; the
 * composer passes its loaded classes so grade/class names render.
 */
export function describeAudience(
  audience: NoticeAudience,
  opts?: {
    classes?: { id: string; name: string; form: string }[];
    studentCount?: number;
  },
): string {
  const classes = opts?.classes ?? [];
  switch (audience.kind) {
    case 'school':
      return 'Whole school';
    case 'grades': {
      const forms = audience.forms ?? [];
      if (forms.length === 0) return 'No grades selected';
      const labels = forms.map((f) => `Form ${f}`);
      return labels.length <= 3
        ? labels.join(' & ')
        : `${labels.length} grades`;
    }
    case 'classes': {
      const ids = audience.classIds ?? [];
      if (ids.length === 0) return 'No classes selected';
      if (ids.length <= 3) {
        return ids
          .map((id) => classes.find((c) => c.id === id)?.name ?? id)
          .join(', ');
      }
      return `${ids.length} classes`;
    }
    case 'students': {
      const n = opts?.studentCount ?? (audience.studentIds ?? []).length;
      return n === 1 ? '1 pupil' : `${n} pupils`;
    }
    default:
      return '';
  }
}
